import { MainLayout } from '@editor/ui-layout';
import { getI18n, useUserStore } from '@editor/ui-utils';
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';

export const LAST_URL_NAME = 'BF_LAST_VISITED_URL';

const routes: RouteRecordRaw[] = [
  // Login must be a standalone top-level route without MainLayout
  {
    path: '/login',
    name: 'login',
    component: () => import('@/components/login-page.view.vue'),
  },
  // Stable parent route for MainLayout; all app routes render inside this layout
  {
    path: '/',
    name: 'main',
    component: MainLayout,
    children: [
      // default redirect
      { path: '', redirect: '/articles' },
    ],
  },
];

const router = createRouter({ history: createWebHistory(), routes });
router.beforeEach((to, from, next) => {
  const i18key = to.meta.i18RouteTitleKey;

  if (to.path !== from.path) {
    // Check if the route exists in i18n messages
    const keyExists = getI18n().te(`${i18key}.routeTitle`);
    document.title = keyExists ? getI18n().t(`${i18key}.routeTitle`) : 'Burda Forward Editor';
  }

  next();
});

// --- Global auth gating guard ---
let bootstrapPromise: Promise<void> | undefined;

router.beforeEach(async (to: RouteLocationNormalized) => {
  // Earliest special-case: handle SSO callback with code query param
  const code = to.query.code;
  if (typeof code === 'string' && code.length) {
    // Preserve deep link before SSO round trip and use callback URL if nothing is saved
    try {
      if (!sessionStorage.getItem(LAST_URL_NAME)) {
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        // Ensure we do not store a URL with the code to prevent loops
        sessionStorage.setItem(LAST_URL_NAME, url.toString());
      }
    } catch (e) {
      console.error(e);
    }

    // Redirect immediately to /login carrying code/state params; do NOT bootstrap user here
    if (to.path !== '/login') {
      const query: Record<string, string> = { code };
      if (typeof to.query.state === 'string' && to.query.state.length) {
        query.state = to.query.state;
      }
      return { path: '/login', query, replace: true };
    }
    // If we are already on /login?code=..., allow to proceed so the login view can handle it
    return true;
  }

  const userStore = useUserStore();

  // 2.1 Bootstrap user once
  if (!userStore.loaded) {
    if (!bootstrapPromise) {
      bootstrapPromise = userStore
        .loadCurrentUser()
        .then(() => void 0)
        .finally(() => {
          // reset promise so a future hard refresh can re-bootstrap if needed
          bootstrapPromise = undefined;
        });
    }
    await bootstrapPromise;
  }

  const isLoginRoute = to.path === '/login';
  const isAuthenticatedAndEnabled = userStore.loggedIn && userStore.isEnabled;

  // 2.3 Redirect unauthenticated/disabled users from protected routes
  if (!isLoginRoute && !isAuthenticatedAndEnabled) {
    // Remember the originally requested destination so we can restore it after the SSO login
    try {
      sessionStorage.setItem(LAST_URL_NAME, new URL(window.location.origin + to.fullPath).toString());
    } catch (e) {
      console.error(e);
    }
    return {
      path: '/login',
      replace: true,
    };
  }

  // allow
  return true;
});

export default router;
